import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetChatbotPromptSettings, type StorePersonality, type ToneStyle } from "@/api/chat-settings/get-chatbot-prompt-settings";
import { useAddChatbotPromptSettings, useUpdateChatbotPromptSettings } from "@/api/chat-settings/update-chatbot-prompt-settings";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const CustomPrompt = () => {
    const { data, isLoading, refetch } = useGetChatbotPromptSettings();
    const { mutate: addSettings, isPending: isAdding } = useAddChatbotPromptSettings();
    const { mutate: updateSettings, isPending: isUpdating } = useUpdateChatbotPromptSettings();

    const [personalityData, setPersonalityData] = useState<Partial<StorePersonality>>({
        company_name: "",
        business_focus: "",
        greetings_examples: "",
        thanks_examples: "",
        repeat_examples: "",
        fallback_response: "",
        off_topic_response: "",
        hook_response: "",
        tone_style: null,
        emoji_enabled: false,
    });
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (data?.data) {
            setPersonalityData({
                company_name: data.data.company_name || "",
                business_focus: data.data.business_focus || "",
                greetings_examples: data.data.greetings_examples || "",
                thanks_examples: data.data.thanks_examples || "",
                repeat_examples: data.data.repeat_examples || "",
                fallback_response: data.data.fallback_response || "",
                off_topic_response: data.data.off_topic_response || "",
                hook_response: data.data.hook_response || "",
                tone_style: data.data.tone_style ?? null,
                emoji_enabled: data.data.emoji_enabled ?? false,
            });
            setIsInitialized(true);
        }
    }, [data]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Prepare payload without the id for new entries
        const payload = {
            company_name: personalityData.company_name || null,
            business_focus: personalityData.business_focus || null,
            greetings_examples: personalityData.greetings_examples || null,
            thanks_examples: personalityData.thanks_examples || null,
            repeat_examples: personalityData.repeat_examples || null,
            fallback_response: personalityData.fallback_response || null,
            off_topic_response: personalityData.off_topic_response || null,
            hook_response: personalityData.hook_response || null,
            tone_style: personalityData.tone_style || null,
            emoji_enabled: personalityData.emoji_enabled ?? false,
        };

        if (data?.data?.id) {
            // Update existing settings using store ID
            updateSettings(payload, {
                onSuccess: () => {
                    toast.success("Chatbot personality settings updated successfully");
                    refetch();
                },
                onError: (error) => {
                    toast.error("Failed to update chatbot personality settings", {
                        description: error.message || "An error occurred while updating settings",
                    });
                },
            });
        } else {
            // Add new settings
            addSettings(payload, {
                onSuccess: () => {
                    toast.success("Chatbot personality settings saved successfully");
                    refetch();
                },
                onError: (error) => {
                    toast.error("Failed to save chatbot personality settings", {
                        description: error.message || "An error occurred while saving settings",
                    });
                },
            });
        }
    };

    const handleChange = (field: keyof StorePersonality, value: any) => {
        setPersonalityData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    if (isLoading && !isInitialized) {
        return <div className="p-4">Loading...</div>;
    }

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle>Store Personality Settings</CardTitle>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                        <Label htmlFor="company_name">Company Name</Label>
                        <Input
                            id="company_name"
                            value={personalityData.company_name || ""}
                            onChange={(e) => handleChange("company_name", e.target.value)}
                            placeholder="visitors to assist"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="business_focus">Business Focus</Label>
                        <Textarea
                            id="business_focus"
                            value={personalityData.business_focus || ""}
                            onChange={(e) => handleChange("business_focus", e.target.value)}
                            placeholder="assisting visitors with information"
                            rows={3}
                        />
                        <p className="text-sm text-muted-foreground">Describe what your business does and its main focus.</p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="greetings_examples">Greetings Examples</Label>
                        <Textarea
                            id="greetings_examples"
                            value={personalityData.greetings_examples || ""}
                            onChange={(e) => handleChange("greetings_examples", e.target.value)}
                            placeholder="Hi there! 👋||Welcome back!||How can I help today?"
                            rows={3}
                        />
                        <p className="text-sm text-muted-foreground">Provide example greetings separated by || (double pipe).</p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="thanks_examples">Thank You Examples</Label>
                        <Textarea
                            id="thanks_examples"
                            value={personalityData.thanks_examples || ""}
                            onChange={(e) => handleChange("thanks_examples", e.target.value)}
                            placeholder="Glad to help!||Thanks for asking!"
                            rows={3}
                        />
                        <p className="text-sm text-muted-foreground">Provide example thank-you messages separated by || (double pipe).</p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="repeat_examples">Repeat/Return Examples</Label>
                        <Textarea
                            id="repeat_examples"
                            value={personalityData.repeat_examples || ""}
                            onChange={(e) => handleChange("repeat_examples", e.target.value)}
                            placeholder="Back for more? Here’s the quick take!"
                            rows={3}
                        />
                        <p className="text-sm text-muted-foreground">
                            How the bot should respond when users repeat questions or return to the conversation.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="fallback_response">Fallback Response</Label>
                        <Textarea
                            id="fallback_response"
                            value={personalityData.fallback_response || ""}
                            onChange={(e) => handleChange("fallback_response", e.target.value)}
                            placeholder="Sorry, don’t have that one! Anything else I can help with? 😊"
                            rows={3}
                        />
                        <p className="text-sm text-muted-foreground">What the bot should say when it doesn't understand the user's input.</p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="off_topic_response">Off-Topic Response</Label>
                        <Textarea
                            id="off_topic_response"
                            value={personalityData.off_topic_response || ""}
                            onChange={(e) => handleChange("off_topic_response", e.target.value)}
                            placeholder="Fun question! I’m here for store info—want me to help with that?"
                            rows={3}
                        />
                        <p className="text-sm text-muted-foreground">How the bot should respond when users go off-topic.</p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="hook_response">Conversational Hook</Label>
                        <Textarea
                            id="hook_response"
                            value={personalityData.hook_response || ""}
                            onChange={(e) => handleChange("hook_response", e.target.value)}
                            placeholder="That work for you? What else ya got?"
                            rows={3}
                        />
                        <p className="text-sm text-muted-foreground">A conversational hook to engage users and keep the conversation flowing.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="tone_style">Tone Style</Label>
                            <Select
                                value={personalityData.tone_style ?? ""}
                                onValueChange={(value: ToneStyle) => {
                                    if (!value) {
                                        return;
                                    }
                                    handleChange("tone_style", value);
                                }}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select tone style" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="casual">Casual</SelectItem>
                                    <SelectItem value="formal">Formal</SelectItem>
                                    <SelectItem value="playful">Playful</SelectItem>
                                    <SelectItem value="professional">Professional</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center space-x-2 pt-6">
                            <Checkbox
                                id="emoji_enabled"
                                checked={!!personalityData.emoji_enabled}
                                onCheckedChange={(checked) => handleChange("emoji_enabled", checked)}
                            />
                            <Label htmlFor="emoji_enabled">Enable emojis in messages</Label>
                        </div>
                    </div>

                    <Button type="submit" disabled={isAdding || isUpdating}>
                        {isAdding || isUpdating ? "Saving..." : "Save Settings"}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
};

export default CustomPrompt;
